import { Head, Link } from '@inertiajs/react'
import { Card } from '../../components/core/Card.jsx'
import { Badge } from '../../components/core/Badge.jsx'
import { Button } from '../../components/core/Button.jsx'
import { StatusDot } from '../../components/core/StatusDot.jsx'
import { Divider } from '../../components/core/Divider.jsx'
import { Statistic } from '../../components/data/Statistic.jsx'
import { MetricBar } from '../../components/data/MetricBar.jsx'
import { Alert } from '../../components/feedback/Alert.jsx'
import { EmptyState } from '../../components/feedback/EmptyState.jsx'
import { withSignedInLayout } from '../../signed_in/SignedInLayout.jsx'
import { BusinessSwitcher } from '../../signed_in/BusinessSwitcher.jsx'
import { quoted } from '~/marketing/marketingData'
import { METHOD_POST, LISTED_POST } from '~/marketing/blog'

// Visibility.for(queries)  -> { named, measurable, named_nobody, not_generated, failed }
// Citedness.for(queries, domain:) -> { domain, cited, measurable, cited_nobody, not_generated, failed }
// Both arrive whole. The page never flattens them to a percentage, never folds an
// exclusion into the figure, and renders an empty denominator as unknown, not 0%.
//
// **Reworked 2026-10-07 against `docs/walkthrough-findings-2026-10-07.md` item 15.**
// Four blocks, each answering one question a customer actually asks, in the order
// they ask them: where do I stand, who with, where did the answers come from, and
// what is not counted. Three things changed structurally.
//
// **Every fact is stated once.** The finding used to render in the `Statistic`
// *and* again as prose ~150px below it, and `measured_on` rendered three times.
// A screen that repeats itself reads as anxious rather than careful.
//
// **The proportion is inverted back.** "What is not in that number" was the
// largest element on the page — four cards, of which three were `0` on a real
// run. Every word of those caveats survives, verbatim, in a disclosure: they are
// the caveats that *are* the product, and `copy-rules.md` is explicit that the
// tone rules never touch them. What changed is how much room they take, which is
// a different decision from whether they are right.
//
// **It answers "so what".** Who was named instead, which engines named them, and
// where the answers came from — all three read from rows that already existed.
//
// No `danger` tone anywhere, and nothing counts down. A low figure is the finding
// the customer signed up to get, not an alarm, and four things stop collection
// without any of them removing anything.
export default function DashboardOverview({
  brand = null,
  // Every business on the account and the plan's business count, for the
  // switcher. Page props on purpose — see `DashboardController#current_brand`.
  businesses = [],
  businesses_included = null,
  visibility = null,
  citedness = null,
  competitors = null,
  engine_visibility = null,
  source_mix = null,
  measured_on = null,
  next_collection_on = null,
  question_count = null,
  commercial_question_count = null,
  collection_state = 'measured',
  // **Re-applied on integration.** The export composed its own sentences again
  // and read none of these, so the dashboard was back to paraphrasing a Class 2
  // string on the main screen of the product — the defect fixed 2026-09-25, and
  // the second half of it is the same phrase that was deleted from the Hub and
  // Spoke grader mockup for being an unsanctioned local copy.
  visibility_summary = null,
  inconclusive_summary = null,
  show_figure = false,
  // Why this figure is not what one person asking would see. Class 2, sent by
  // the controller with the two sentences above.
  measurement_basis = null,
  // From `Billing::Summary`, under the same names `/billing` uses for the same
  // fact. See the `empty` branch below for what they fixed.
  stop = 'none',
  stop_sentence = null,
  // What the business can do about this week, from `NextSteps`: the
  // directories, review sites, roundups and competitors' pages the AI
  // assistants drew on, and whether the business's own site was used. Null
  // before a measurement.
  next_steps = null,
}) {
  const collecting = collection_state === 'collecting'
  // **The third state the controller has always sent and this page never read.**
  // `collection_state` has been `collecting` / `empty` / `measured` since it was
  // written, and its own comment says why the last two differ: *"`empty` means
  // nothing has ever been collected, which reads differently from a measurement
  // of zero."* The page branched on `collecting` alone, so `empty` fell through
  // to the measured layout and rendered the inconclusive Class 2 sentence —
  // "No answer recommended a business when we last measured" — for a brand with
  // no runs at all. Reachable, and invited: `Auth/Verify` offers "Carry on
  // without confirming", and a held account has exactly no runs.
  const nothingCollected = collection_state === 'empty'
  const measurable = visibility && visibility.measurable
  const known = !collecting && visibility != null && measurable != null && measurable > 0

  return (
    <>
      <Head title="Overview" />
      <main className="mx-auto min-h-screen w-full max-w-5xl px-6 py-10">
        <header className="mb-8 flex flex-wrap items-end justify-between gap-4">
          <div className="flex min-w-0 flex-col gap-2">
            <h1 className="page-title">Overview</h1>
            {brand && (
              <p className="text-[13.5px] text-[color:var(--text-muted)]">
                {brand.name} · {brand.industry} · {brand.city}, {brand.state}
              </p>
            )}
          </div>
          {/*
            **The only route to business settings in the product.** That page
            permits the website and the other names — the two things a customer
            corrects after setup — and until 2026-10-10 nothing linked to it at
            any width, so it was reachable by typing a URL and in no other way.

            It belongs on this line rather than in the header nav, and the line
            above is the argument: `name · industry · city, state` is exactly
            the set of facts that page either edits or explains as
            unchangeable, so the affordance sits on the fact it changes. The
            header is account-scoped — Overview, Questions, Plan, Account are
            all account-level — and a brand-scoped item in it would need the
            layout to carry a brand id as a *shared* prop, which is the
            collision that put "Day 6 of 6" in the header on 2026-10-03.
          */}
          <div className="flex flex-wrap items-center gap-4">
            {collecting && (
              <span className="flex items-center gap-2 text-[13.5px] text-[color:var(--text-muted)]">
                <StatusDot state="visible" pulse label="Collecting" />
              </span>
            )}
            {brand && (
              <BusinessSwitcher businesses={businesses} included={businesses_included} path="/dashboard" />
            )}
            {brand && (
              <Button
                as={Link}
                href={`/brands/${brand.id}/edit`}
                variant="ghost"
                size="sm"
                iconLeft="settings"
              >
                Business settings
              </Button>
            )}
          </div>
        </header>

        {!brand && (
          <EmptyState icon="map-pin" title="No business configured yet">
            Add a business, its industry and its city. Those three determine the twenty questions each
            AI assistant is asked.
          </EmptyState>
        )}

        {brand && collecting && (
          <div className="flex flex-col gap-6">
            <Alert tone="data" icon="activity" title="First collection in progress">
              We are asking each question of every AI assistant, three times each. Every number here is
              counted from a real answer, and appears once it has been.
            </Alert>
            <Card variant="placeholder" pad="lg">
              {/* **No `noun` here, and that is a correction rather than an
                  omission.** `Statistic` renders `noun` only inside its
                  denominator block, which both `placeholder` and `unknown` skip —
                  so the word was being passed and silently dropped, the same
                  contract trap that was rendering a bare "1" on three of the four
                  exclusions. There is no denominator in this state, so there is
                  nothing for the noun to qualify; the badge says "not measured"
                  and the alert above says why. The component is not edited here.

                  **`lg` rather than `xl`, and the system's own behaviour is the
                  argument.** `sp-stat--unknown` already overrides the figure to a
                  30px dashed pill at body size — so a state with no number
                  *already* gives up the display scale, and `placeholder` was the
                  one that did not. At `xl` the badge computes
                  `align-self: flex-start` and pins to the top of a 44px row while
                  the em-dash puts its ink 10.8-14.9px above the baseline, where a
                  digit reaches 31.9px: 26px between their bottoms, reading as two
                  loose objects. At `lg` that halves to 14px and they read as one.
                  Measured against the real stylesheet and the real face, because a
                  comparison rendered in a fallback font is a comparison of the
                  wrong typeface. `xl` stays where the figure *is* the finding. */}
              <Statistic label="Named in" state="placeholder" size="lg" />
            </Card>
          </div>
        )}

        {/*
          Nothing collected, and nothing in flight. The honest screen says which
          and why, in that order, rather than reporting a measurement.

          **`placeholder`, not `unknown`, and the design system draws the
          distinction for exactly this.** Its own comment: *"unknown (no
          measurement possible)"* against *"placeholder (not measured yet)"*. The
          `!known` branch below is the first; this is the second, and rendering
          one as the other is how "we have not asked" reads as "we asked and found
          nothing".

          The body is the stop sentence where there is one — held is the common
          case by a distance — and the next attempt where there is not, which is
          the engine-outage shape: a run settled with every query failed or
          skipped, so `last_collected_at` is written and the sweep comes round
          again. Where we know neither, the title stands alone rather than being
          restated in longer words.
        */}
        {brand && nothingCollected && (
          <div className="flex flex-col gap-6">
            <Alert
              tone="data"
              icon={stop === 'held' ? 'mail' : 'calendar'}
              title="Nothing collected yet"
              actions={
                stop_sentence ? (
                  <Button variant="secondary" size="sm" href={stop === 'held' ? '/verify' : '/billing'}>
                    {stop === 'held' ? 'Confirm your email' : stop === 'no_plan' ? 'Choose a plan' : 'See your plan'}
                  </Button>
                ) : undefined
              }
            >
              {stop_sentence ||
                (next_collection_on ? `We ask again on ${next_collection_on}.` : null)}
            </Alert>
            <Card variant="placeholder" pad="lg">
              {/* Identical to the collecting card above, deliberately — the two
                  say the same thing about the same figure and differ only in why.
                  See that comment for the `lg` and the absent `noun`. */}
              <Statistic label="Named in" state="placeholder" size="lg" />
            </Card>
          </div>
        )}

        {brand && !collecting && !nothingCollected && (
          <div className="flex flex-col gap-6">
            <Card variant="signal" pad="lg">
              <div className="flex flex-col gap-6">
                {/*
                  Three states, and the middle one is the fix.

                  `show_figure` comes from `VisibilitySummary#figure?` and is true
                  only where the big `{named} of {measurable}` *is* the sanctioned
                  sentence. It used to render unconditionally, so a business named
                  nowhere read "Named in / 0 of 72 / answers that recommended a
                  business" — the plural sentence with a nought in it, which is
                  exactly what the zero variant exists to replace.

                  **The prose repeat is gone and that is not a loss of the Class 2
                  string.** When `show_figure` is true the `Statistic` renders the
                  sanctioned sentence *as a layout* — label, figure, noun, in that
                  order and in those words — so printing it again underneath said
                  one thing twice. When it is false the sentence is the only thing
                  rendered, as prose. Either way it appears exactly once, and it
                  arrives written from `VisibilitySummary`, which is also what the
                  grader's report, the grader's email and the first-result email
                  read.
                */}
                {known && show_figure && (
                  <Statistic
                    label="Named in"
                    value={visibility.named}
                    of={visibility.measurable}
                    noun="answers that recommended a business"
                    size="xl"
                    tone="signal"
                    date={measured_on || undefined}
                  />
                )}
                {known && !show_figure && (
                  <div className="flex flex-col gap-3">
                    <p className="max-w-[46ch] text-[22px] font-semibold leading-snug text-[color:var(--text-strong)]">
                      {visibility_summary}
                    </p>
                    {measured_on && (
                      <p className="font-mono text-[12px] text-[color:var(--text-muted)]">
                        Measured {measured_on}
                      </p>
                    )}
                  </div>
                )}
                {!known && (
                  <>
                    {/* `noun` dropped for the reason given on the placeholder
                        above: `unknown` renders no denominator, so the word never
                        reached the page. The sentence underneath is the Class 2
                        one and carries it. */}
                    <Statistic
                      label="Named in"
                      state="unknown"
                      size="xl"
                      date={measured_on || undefined}
                    />
                    {/*
                      The unknown sentence is Class 2 and **arrives written**,
                      since 2026-09-29. It used to be composed here as "No answer
                      recommended a business, so there was nothing to be named in.
                      That reads unknown, not zero." — a paraphrase whose second
                      half explains our own rendering rule to a customer. It came
                      in from the Hub and Spoke grader mockup, where the same
                      phrase sat in a local INCONCLUSIVE constant that was deleted
                      on integration for being an unsanctioned local copy; the tail
                      survived here, one screen over. No fallback is kept either —
                      a fallback for a Class 2 string is a second copy, and a
                      second copy is how a corrected sentence keeps rendering in
                      its old form.
                    */}
                    <p className="max-w-[62ch] text-[13.5px] text-[color:var(--text-body)]">
                      {inconclusive_summary}
                    </p>
                  </>
                )}

                {/*
                  **The question a customer is likeliest to bring to the figure**
                  — "I asked ChatGPT and it named me" — answered beside it, in
                  one line, 2026-10-14. The sanctioned short form rather than the
                  full measurement basis: the 2026-10-07 rework shrank the caveats
                  because they had outgrown the findings, and the link carries
                  the rest for anybody who wants it.
                */}
                {known && measurement_basis && (
                  <p className="max-w-[62ch] text-[13px] text-[color:var(--text-muted)]">
                    {measurement_basis}{' '}
                    <Link href={METHOD_POST} className="underline underline-offset-4">
                      How these numbers are counted
                    </Link>
                  </p>
                )}

                <Standings brand={brand} visibility={visibility} competitors={competitors} known={known} />
              </div>
            </Card>

            {/*
              **Why collection has stopped, where the next date would be** —
              2026-10-30, Next Steps #29. The date appears only when a
              collection is promised, so a lapsed trial, a cancelled plan, a
              failed payment or a used-up allowance showed last week's reading
              looking current, with the reason only on the Plan page. The stop
              sentence is the sanctioned one `/billing` prints, in the same
              neutral Alert, never danger and never a countdown.

              **It replaces the date rather than sitting beside it.** The date
              asks whether the trial or plan is live and not about the
              allowance, so with the allowance used up it could promise a
              collection the sweep will skip.
            */}
            {stop && stop_sentence && (
              <Alert
                tone="neutral"
                icon={stop === 'past_due' ? 'credit-card' : stop === 'held' ? 'mail' : 'info'}
                title={stop === 'held' ? 'Collection has not started' : 'Collection is not running'}
                actions={
                  <Button variant="secondary" size="sm" href={stop === 'held' ? '/verify' : '/billing'}>
                    {stop === 'held' ? 'Confirm your email' : 'See your plan'}
                  </Button>
                }
              >
                {stop_sentence}
              </Alert>
            )}

            {next_collection_on && !(stop && stop_sentence) && (
              <p className="max-w-[72ch] text-[13.5px] text-[color:var(--text-muted)]">
                We ask again on{' '}
                <span className="font-mono text-[color:var(--text-body)]">{next_collection_on}</span>, and
                this reading is replaced by that one. {quoted.reading_moves}
              </p>
            )}

            {/* The page reads top to bottom as how you're doing, what to do
                about it, your own site, and how the figure is counted —
                2026-10-28, on the founder's review. Each AI assistant's result
                breaks down the figure above it, so it comes before the steps;
                the sources are the full list the steps name their sites from,
                so they follow the steps rather than sitting under "Your own
                site", which they are not about. */}
            <Engines rows={engine_visibility && engine_visibility.rows} />

            {known && <NextSteps steps={next_steps} brandId={brand.id} />}

            <Sources mix={source_mix} ownDomain={brand?.domain} />

            <Divider label="Your own site" />

            <Card title="Cited as a source" description="A different question, on a different denominator">
              <div className="flex flex-col gap-4">
                {citedness && citedness.domain ? (
                  <>
                    <Statistic
                      label="Cited in"
                      value={citedness.measurable > 0 ? citedness.cited : undefined}
                      of={citedness.measurable > 0 ? citedness.measurable : undefined}
                      state={citedness.measurable > 0 ? 'measured' : 'unknown'}
                      noun="answers that cited any source"
                      size="lg"
                      tone="data"
                    />
                    <p className="max-w-[62ch] text-[13.5px] text-[color:var(--text-muted)]">
                      Measured across every question, not only the ones that ask who to use, and
                      matched against {citedness.domain}. Being named is about who gets recommended;
                      being cited is about which pages an answer was built from. They are separate
                      findings and never share a number.
                    </p>
                  </>
                ) : (
                  <>
                    {/* Same correction: `unknown` draws no denominator, so "answers"
                        was never rendered. The prose below says what is missing. */}
                    <Statistic label="Cited in" state="unknown" size="lg" />
                    {/*
                      This read "No website has been entered, so there is
                      nothing to match the cited sources against. Add one and
                      this is measured from the answers already collected." Two
                      absences as subjects before the reader reaches the one
                      thing they can do — tone rule 1. The gain leads now, and
                      the figure above already says `unknown`, so the sentence
                      does not have to say it a second time.
                    */}
                    <p className="max-w-[62ch] text-[13.5px] text-[color:var(--text-muted)]">
                      Add your website and we can tell you whether your own pages are being used as
                      sources, measured from the answers already collected.
                    </p>
                    {/* Business settings, where the website is entered. This
                        pointed at the account page, which has no website field,
                        until 2026-10-14. */}
                    <div>
                      <Button variant="secondary" size="sm" href={`/brands/${brand.id}/edit`}>
                        Add your website
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </Card>

            <Exclusions
              visibility={visibility}
              questionCount={question_count}
              commercialCount={commercial_question_count}
            />

            <p className="text-[13.5px] text-[color:var(--text-muted)]">
              <Link href="/dashboard/prompts" className="underline underline-offset-4">
                See the questions this is built from
              </Link>
            </p>
          </div>
        )}
      </main>
    </>
  )
}

// Who was recommended instead, on the same denominator as the figure above.
//
// **The customer's own row sits in the same list, sorted by count.** Two reasons.
// A figure with nothing beside it cannot be read — "named in 13 of 27" is good or
// bad depending entirely on what the alternative was, and that is exactly what
// `Competitors` now answers. And putting them in the list rather than above it
// means the screen never has to characterise their position: a reader can see
// where they are without being told they are behind.
//
// `visibility.measurable` is the max for every bar, so one denominator governs
// the block and no row can imply a different one. `Competitors.measurable` is
// computed from the same scope and asserted equal in `competitors_test`.
/*
  "What you can do", under the figure — 2026-10-28, a mockup for the founder's
  review. One step per kind of site, each built from this week's answers and
  naming the sites in them, rather than the advice every business gets. Advice
  only: the card says where the AI assistants looked, never that we will act,
  and never that a step will move the number. A step with nothing to name is
  left out, and "keep your details the same" appears only beside a step that
  names listings.
*/
function NextSteps({ steps, brandId }) {
  if (!steps) return null

  const one = (sites, single, several) => (sites.length === 1 ? single : several)
  const items = []

  if (steps.directory_sites.length > 0) {
    const d = steps.directory_sites
    items.push({
      title: 'Get listed on the directories AI assistants used',
      body: (
        <>
          This week, AI assistants used {one(d, 'the directory', 'the directories')} {siteList(d)} when
          they answered your questions. Check that your business is listed on {one(d, 'it', 'each one')}.
        </>
      ),
      link: { href: LISTED_POST, label: 'How to get listed where AI assistants look' },
    })
  }
  if (steps.review_sites.length > 0) {
    const r = steps.review_sites
    items.push({
      title: 'Reply to reviews on the sites AI assistants used',
      body: (
        <>
          This week, AI assistants used {one(r, 'the review site', 'the review sites')} {siteList(r)} when
          they answered your questions. Check that your business has a profile on {one(r, 'it', 'each one')},
          keep the details on it current, and reply to the reviews customers leave there.
        </>
      ),
    })
  }
  if (steps.directory_sites.length > 0 || steps.review_sites.length > 0) {
    items.push({
      title: 'Keep your business details the same everywhere',
      body: (
        <>
          Use the same business name, address, phone number, hours and services on every directory and
          review site as on your website, so customers and AI assistants find the same details wherever
          they look. When any of them change, update every listing at the same time.
        </>
      ),
    })
  }
  if (steps.roundup_sites.length > 0) {
    const u = steps.roundup_sites
    items.push({
      title: 'Get into the roundups AI assistants used',
      body: (
        <>
          This week, AI assistants used{' '}
          {one(u, 'a roundup article, a list of recommended local businesses,', 'roundup articles, lists of recommended local businesses,')}{' '}
          on {siteList(u)} when they answered your questions. Read how{' '}
          {one(u, 'the article chooses', 'each article chooses')} the businesses it includes. Some roundups
          accept submissions or suggestions from businesses.
        </>
      ),
      link: { href: LISTED_POST, label: 'How to get listed where AI assistants look' },
    })
  }
  if (steps.competitor_pages.length > 0) {
    const c = steps.competitor_pages
    items.push({
      title: "Cover what competitors' websites cover",
      body: (
        <>
          This week, AI assistants used pages from {one(c, "a competitor's website", "competitors' websites")},{' '}
          {siteList(c)}, when they answered your questions. Read what those pages cover. If one of them
          answers a question that your website doesn't, write a page on your own website that answers it.
        </>
      ),
      link: { href: '/dashboard/prompts', label: 'See the questions AI assistants were asked' },
    })
  }
  if (steps.own_site === 'none') {
    items.push({
      title: 'Add your website',
      body: (
        <>
          Add your website to this business's settings. We will then show you whether AI assistants use
          it as a source when they answer your questions.
        </>
      ),
      link: { href: `/brands/${brandId}/edit`, label: 'Open business settings' },
    })
  } else if (steps.own_site === 'not_cited') {
    items.push({
      title: 'Give AI assistants a page of yours to use',
      body: (
        <>
          This week, AI assistants drew on other websites when they answered your questions. A page on
          your website for this location, covering the city you serve, your services, your hours and the
          questions customers ask, gives them a page of yours to use.
        </>
      ),
    })
  }

  if (items.length === 0) return null

  return (
    <Card title="What you can do" description="Based on this week's answers from AI assistants">
      <ol className="flex flex-col gap-5">
        {items.map((item, i) => (
          <li key={item.title} className="flex gap-4">
            <span className="font-mono text-[13px] tabular-nums text-[color:var(--text-muted)]">
              {String(i + 1).padStart(2, '0')}
            </span>
            <div className="flex flex-col gap-1.5">
              <p className="sp-card__title">{item.title}</p>
              <p className="max-w-[64ch] text-[13.5px] text-[color:var(--text-body)]">{item.body}</p>
              {item.link && (
                <p className="text-[13px]">
                  <Link href={item.link.href} className="underline underline-offset-4">
                    {item.link.label}
                  </Link>
                </p>
              )}
            </div>
          </li>
        ))}
      </ol>
    </Card>
  )
}

// "a", "a and b", "a, b and c" — the domains in mono, as the sources card prints them.
function siteList(sites) {
  const mono = (s) => <span key={s} className="font-mono text-[color:var(--text-strong)]">{s}</span>
  if (sites.length === 1) return mono(sites[0])
  const head = sites.slice(0, -1)
  return (
    <>
      {head.map((s, i) => (
        <span key={s}>{i > 0 && ', '}{mono(s)}</span>
      ))}
      {' and '}
      {mono(sites[sites.length - 1])}
    </>
  )
}

function Standings({ brand, visibility, competitors, known }) {
  if (!known || !competitors || !competitors.rows || competitors.rows.length === 0) return null

  const total = visibility.measurable
  const rows = [{ name: brand.name, named: visibility.named, you: true }]
    .concat(competitors.rows.map((r) => ({ ...r, you: false })))
    .sort((a, b) => b.named - a.named || a.name.localeCompare(b.name))

  return (
    <div className="flex flex-col gap-4 border-t border-[color:var(--border-subtle)] pt-5">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-[13.5px] font-medium text-[color:var(--text-strong)]">
          Who else was recommended
        </p>
        <p className="text-[13px] text-[color:var(--text-muted)]">
          {competitors.distinct_businesses === 1
            ? 'One other business was named across these answers'
            : `${competitors.distinct_businesses} other businesses were named across these answers`}
        </p>
      </div>
      <div className="flex flex-col gap-3">
        {rows.map((row) => (
          <MetricBar
            key={row.you ? '__you__' : row.name}
            name={row.name}
            value={row.named}
            max={total}
            display={`${row.named} of ${total}`}
            tone={row.you ? 'signal' : 'muted'}
          />
        ))}
      </div>
      <p className="max-w-[62ch] text-[13px] text-[color:var(--text-muted)]">
        Counted over the same answers as your own figure, so the rows are comparable. A business
        named twice in one answer is counted once, the same way you are.
      </p>
    </div>
  )
}

// Which engines named them. The actionable half of one pooled number.
//
// **Ordered by the product's dispatch order rather than by score**, decided in
// `EngineVisibility` — a list that reshuffles weekly cannot be read weekly. Which
// engines named them is said by tone, not by position.
//
// An engine with nothing measurable renders *unknown* rather than zero, and that
// state is routine rather than exceptional: Google shows no AI Overview for
// roughly 71% of local commercial searches, so its denominator is often the small
// one and can legitimately be empty. It did not decline to recommend them — there
// was no answer to be recommended in.
function Engines({ rows }) {
  if (!rows || rows.length === 0) return null

  const anyUnknown = rows.some((row) => !(row.visibility.measurable > 0))

  return (
    <Card title="Which AI assistants named you" description="The same finding, one AI assistant at a time">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-3">
          {rows.map((row) => {
            const v = row.visibility
            const measured = v.measurable > 0
            return (
              <MetricBar
                key={row.engine}
                name={row.label}
                value={v.named}
                max={measured ? v.measurable : 1}
                display={measured ? `${v.named} of ${v.measurable}` : undefined}
                state={measured ? 'measured' : 'unknown'}
                tone={measured && v.named > 0 ? 'data' : 'muted'}
              />
            )
          })}
        </div>
        {/*
          **The second sentence is gated on the state it explains**, 2026-10-10.
          It rendered unconditionally, so a customer whose five engines all
          measured something read an explanation of an `unknown` row that was
          not on the screen — and `copy-rules.md`'s own test is *would a reader
          have wondered this if we had not raised it?* They would not; there is
          nothing to wonder about. Same correction as the retention block on
          `/billing`, which answered an exit question at the moment of joining.

          It stays unsoftened where an engine *is* unknown, because that is the
          caveat doing its job: a reader meeting a row with no number has to be
          told it is an absent measurement rather than a nil result.
        */}
        <p className="max-w-[62ch] text-[13px] text-[color:var(--text-muted)]">
          Each AI assistant has its own denominator, so the counts are shown in full rather than as
          one share.
          {anyUnknown
            ? ' An AI assistant reading unknown produced no answer that recommended anybody, which says nothing about you either way.'
            : ''}
        </p>
        {/* Whether this is measured in their city, said where each AI
            assistant is listed, 2026-10-14. Class 2, from `quoted`. */}
        <p className="max-w-[62ch] text-[13px] text-[color:var(--text-muted)]">{quoted.google_method}</p>
      </div>
    </Card>
  )
}

// Where the answers came from, split by what a business can do about it.
//
// **The split arrives from the server and is never re-derived here.** Which kinds
// are actionable is a product decision in
// `SourceDomainClassifier::ACTIONABLE_KINDS`; a page grouping the rows itself
// would be a second copy of that list.
//
// **The two halves are deliberately described in different words**, because
// `PROJECT_STATE` is explicit that *"getting listed on a directory and writing a
// missing page are different products"*. Collapsing them into one "opportunities"
// count would be the error that sentence exists to prevent.
//
// **Everything here says what to change and never that we will change it.** That
// is a billing constraint rather than a style preference: Managed Payments
// requires a fully automated digital product and excludes marketing services by
// name, so advice is what this product may offer. The internal name for the
// unbuilt feature this foreshadows does not appear on the screen.
function Sources({ mix, ownDomain }) {
  if (!mix || !mix.domains) return null

  const listings = sumDomains(mix.listings)
  const content = sumDomains(mix.content)
  const other = sumDomains(mix.other)
  const sites = mix.sites || {}

  return (
    <Card
      title="Where the answers came from"
      description={`${mix.domains} ${mix.domains === 1 ? 'site' : 'sites'} across ${mix.citations} ${
        mix.citations === 1 ? 'citation' : 'citations'
      }`}
    >
      <div className="grid gap-6 sm:grid-cols-3">
        <SourceGroup
          value={listings}
          title="Places that can list you"
          body="Directories, review sites, forums, news and reference pages the answers drew on. A business can appear on these."
          sites={sites.listings}
          total={sites.listings_total}
        />
        <SourceGroup
          value={content}
          title="Businesses' own pages"
          body="The answers also drew on businesses' own websites. A page there that covers something yours doesn't is a page worth writing."
          sites={sites.content}
          total={sites.content_total}
          ownDomain={ownDomain}
        />
        <SourceGroup
          value={other}
          title="Everything else"
          body="Sites that fit neither group, and any we have not classified yet."
        />
      </div>
    </Card>
  )
}

// **The unit goes in `label`, not in `noun`.** `Statistic` renders `noun` only
// inside its denominator block, so a figure passed `noun` and no `of` drops the
// word silently — "6" with nothing saying six of what. That is a contract fact
// about a design-system component, which is not edited here, so the page works
// with it rather than around it. The same trap had already shipped on this page's
// exclusions, where three of the four figures lost their noun; see `Exclusion`.
function SourceGroup({ value, title, body, sites, total, ownDomain }) {
  return (
    <div className="flex flex-col gap-2">
      <Statistic label={value === 1 ? 'Site' : 'Sites'} value={value} size="sm" />
      <p className="text-[13.5px] font-medium">{title}</p>
      <p className="max-w-[44ch] text-[13px] text-[color:var(--text-muted)]">{body}</p>
      {sites && sites.length > 0 && <SiteList sites={sites} total={total} ownDomain={ownDomain} />}
    </div>
  )
}

// **The sites by name**, 2026-10-26: the count above says how many, and the
// action — get listed here, write a page like this — needs to know which. The
// free report's list, in the free report's layout, most-cited first and cut
// at ten with the rest counted. Inline sizes rather than utilities, because a
// size utility on an element `base.css` styles is the cascade-layer landmine.
//
// **The customer's own site is marked, not removed.** It is a business's own
// page, so it belongs in this group and its count — but the home page calls the
// group "the competitors' pages they cite", and unmarked it reads as a rival.
// Found in the sandbox, whose own domain was the group's first row.
function SiteList({ sites, total, ownDomain }) {
  return (
    <>
      <ul style={{ listStyle: 'none', padding: 0, margin: '4px 0 0', display: 'grid', gap: 6 }}>
        {sites.map((s) => (
          <li
            key={s.domain}
            style={{ display: 'flex', justifyContent: 'space-between', gap: 12, borderTop: '1px solid var(--border-subtle)', paddingTop: 6 }}
          >
            {/* "your site" under the domain rather than beside the count: beside
                it, the count column grew and broke a phone-width domain mid-word. */}
            <span style={{ display: 'grid', gap: 2 }}>
              <span className="sp-mono" style={{ fontSize: 12.5, color: 'var(--text-strong)', overflowWrap: 'anywhere' }}>{s.domain}</span>
              {s.domain === ownDomain && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Your site</span>}
            </span>
            <span className="sp-mono" style={{ fontSize: 12, color: 'var(--text-muted)', flex: 'none' }}>
              {s.citations === 1 ? 'cited once' : `cited ${s.citations} times`}
            </span>
          </li>
        ))}
      </ul>
      {total > sites.length && <span className="sp-caption">And {total - sites.length} more.</span>}
    </>
  )
}

// What is not in that number — every word kept, in a tenth of the room.
//
// **This was the largest element on the page and is now a disclosure.** The
// caveats are correct and load-bearing: each exclusion left in would move the
// figure in the customer's favour, which this project records as the direction
// hardest to defend. `copy-rules.md` is equally explicit that the tone rules
// *"do not touch the caveats that are the product"*. So nothing here is softened
// or shortened. It is collapsed, which is a decision about proportion rather than
// about accuracy, and the summary line states the total so the disclosure is not
// where a number goes to hide.
//
// **An exclusion that excluded nothing is omitted rather than rendered as zero.**
// Three of the four were `0` on a real run, and four cards of which three say
// zero is what made this read like a compliance form. The purpose of reporting an
// exclusion is to account for answers removed from the denominator, so where none
// were removed there is nothing to account for. A *null* still renders as unknown:
// that is "we cannot say", which is a different fact from zero and the whole rule
// this screen exists to hold.
//
// **The first body is the sanctioned string and used to be a withdrawn
// paraphrase.** It read "Counting them would move the number without telling you
// anything about it" — the exact clause `copy-rules.md` rewrote on 2026-10-05 for
// stating that something moves and never saying what. `copy_rules_test` reads
// `marketingData.js` and could not see a second copy living in a page, so the
// withdrawn wording went on shipping here. It is imported from the one place that
// string lives, which puts it under that guard.
//
// **The second body was the same shape and nothing had noticed**, fixed
// 2026-10-09 with item 16. It carried *"There was nothing to be named in, so they
// are not counted above."* as a literal — which is the tail of
// `quoted.recommended_no_one`, so this page held a second copy of half a
// sanctioned string. Being half of one is what made it invisible twice over: no
// guard compares a page against the authority, and the plural "they" is wrong at
// n = 1, which is the exact correction that was already sanctioned and would
// have landed in `marketingData.js` alone.
function Exclusions({ visibility, questionCount, commercialCount }) {
  const nonCommercial = nonCommercialCount(questionCount, commercialCount)

  const items = [
    {
      key: 'questions',
      value: nonCommercial,
      of: questionCount,
      noun: 'questions',
      title: 'Questions that do not ask who to use',
      body:
        questionCount != null && commercialCount != null
          ? quoted.questions_basis(questionCount, commercialCount)
          : null,
    },
    {
      key: 'named_nobody',
      value: visibility && visibility.named_nobody,
      noun: 'answers',
      title: 'Answers that recommended no business',
      body: visibility && visibility.named_nobody
        ? tailOf(quoted.recommended_no_one(visibility.named_nobody))
        : null,
    },
    {
      key: 'not_generated',
      value: visibility && visibility.not_generated,
      noun: 'searches',
      title: 'Searches with no AI Overview',
      // **The third was the same shape again**, fixed 2026-10-09 with item 17.
      // It held `google_none`'s first sentence verbatim and then went its own
      // way: *"These are kept out of your denominator"* where the sanctioned
      // string says *"We record those as their own outcome and keep them out of
      // your denominator"*. `google_none` is the exact string that drifted on
      // 2026-10-04 and caused `copy_rules_test` to be written, so this is that
      // story one screen over — and the dropped clause is the one that says what
      // the card above it *is*.
      body: quoted.google_none,
    },
    {
      key: 'failed',
      value: visibility && visibility.failed,
      noun: 'requests',
      title: 'Requests that did not complete',
      body:
        'The answer was never read, so it is absent from the denominator rather than counted against your results.',
    },
  ].filter((item) => item.value == null || item.value > 0)

  if (items.length === 0) return null

  const excluded = items
    .filter((item) => item.key !== 'questions' && typeof item.value === 'number')
    .reduce((sum, item) => sum + item.value, 0)

  // **Open, with no toggle** — decided by the founder 2026-10-10, after the
  // padding fix below made the collapsed row legible enough to look at
  // properly. It was a `<details>` from the 2026-10-07 rework, and what that
  // rework was actually correcting was *proportion*: four cards, three of them
  // reading `0` on a real run, were the largest element on the page. Omitting
  // an exclusion that excluded nothing already fixed that — three items in a
  // two-column grid is a fraction of what it replaced — so the collapsing was
  // buying very little on top, at the cost of a control in front of the
  // caveats that *are* the product. Every word is still verbatim; this only
  // stops them being behind a click.
  //
  // The chevron went with it. It existed because a `<summary>` that looks like
  // a heading is a control nobody presses — found in the browser pass that
  // shipped the rework — and with nothing to open, the affordance has nothing
  // to afford.
  //
  // **`sp-card--pad-md` is not decoration — without it this card has no padding
  // at all**, and that is a contract fact about the design system rather than a
  // taste call. `.sp-card` sets the background, border and radius and nothing
  // else; padding comes from a `--pad-*` modifier or from a `.sp-card__body`
  // child, and `Card` supplies one by defaulting to `pad="md"`. This is the only
  // hand-written `sp-card` in the app, so it is the only place that has to say
  // so itself — and it did not, so the row ran flush to the border on both
  // sides while every sibling card on the page sat 20px in.
  //
  // Third instance of this family: `Statistic` renders `noun` only inside its
  // denominator block, and `SourceGroup` carries the same note. A composed
  // primitive silently drops what its markup does not ask for.
  return (
    <section className="sp-card sp-card--inset sp-card--pad-md">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        {/*
          **`sp-card__title`, not a Tailwind text utility, and the reason is a
          cascade trap rather than a style preference.** Tailwind 4 emits its
          utilities inside `@layer utilities`, while the design system's
          `base.css` is unlayered — and an unlayered declaration beats a layered
          one *whatever the specificity*. So `h2{font-size:var(--fs-h2)}` wins
          against `text-[13.5px]`, and this heading rendered at 28px against
          the 15px of every sibling card title. Nothing warns: the class is in
          the bundle, the element carries it, and it is simply outranked.

          It reaches every element `base.css` styles — `h1`-`h5`, `a`, `p`,
          `code` — so a text utility on any of those is a coin toss. The system's
          own class is unlayered too, so it wins on specificity and matches the
          cards above by construction rather than by a number copied from them.
        */}
        <h2 className="sp-card__title">What the “Named in” figure leaves out</h2>
        <span className="text-[13px] text-[color:var(--text-muted)]">
          {excluded > 0
            ? `${excluded} ${excluded === 1 ? 'answer' : 'answers'} reported here rather than counted in the figure`
            : 'Reported here rather than counted in the figure'}
        </span>
      </div>
      <div className="mt-5 grid gap-5 sm:grid-cols-2">
        {items.map((item) => (
          <Exclusion key={item.key} {...item} />
        ))}
      </div>
    </section>
  )
}

// `noun` is passed only where there is a denominator to hang it on, and becomes
// the `label` otherwise — see the note on `SourceGroup`. Before this, three of
// the four exclusions rendered a bare "1" with the word dropped, so "1 answer
// recommended no business" read as "1".
//
// **The label is singular at one, the title is not**, and the asymmetry is
// deliberate. A label sits directly above its own figure and reads as part of
// it — "1 / Answers" is the "1 answers" defect in a layout. A title names the
// category the figure belongs to, the way a column heading does, so it stays
// plural at any count including zero.
function Exclusion({ value, of, noun, title, body }) {
  const measured = value != null
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-baseline gap-2">
        {measured ? (
          <Statistic
            value={value}
            of={of}
            noun={of != null ? noun : undefined}
            label={of == null ? capitalise(singularise(noun, value)) : undefined}
            size="sm"
          />
        ) : (
          <Badge tone="unknown">unknown</Badge>
        )}
      </div>
      <p className="text-[13.5px] font-medium">{title}</p>
      {body && <p className="max-w-[48ch] text-[13px] text-[color:var(--text-muted)]">{body}</p>}
    </div>
  )
}

function sumDomains(rows) {
  if (!rows || rows.length === 0) return 0
  return rows.reduce((sum, row) => sum + row.domains, 0)
}

function capitalise(word) {
  if (!word) return undefined
  return word.charAt(0).toUpperCase() + word.slice(1)
}

// Every noun on this screen is a regular plural — answers, searches, requests,
// questions — so dropping the "s" is the whole rule, and it is applied rather
// than generalised. A noun that needed more than that would be a reason to pass
// both forms in, not a reason to grow an inflector here.
function singularise(noun, count) {
  if (!noun || count !== 1) return noun
  return noun.replace(/e?s$/, '')
}

// Everything after the first sentence of a sanctioned string, for a surface that
// renders the opening clause as a layout — label, figure, noun, in those words —
// and needs only the rest of it as prose.
//
// **Sliced off the string rather than retyped**, because a literal here is a
// second copy of half a Class 2 string, which is the drift `copy-rules.md`
// exists to prevent and the one shape no guard in this repo can see. Slicing
// also carries the singular for free: the sanctioned n = 1 form ends "so it is
// not counted above", and a retyped tail is exactly where that correction would
// have gone missing.
function tailOf(sentence) {
  const end = sentence.indexOf('. ')
  return end === -1 ? sentence : sentence.slice(end + 2)
}

function nonCommercialCount(total, commercial) {
  if (total == null || commercial == null) return null
  return total - commercial
}

DashboardOverview.layout = withSignedInLayout
